/**
 * VersandCockpit — per-record case hub.
 *
 * Layout (revamped):
 *   1. Header (back, record title, "Datensatz öffnen")
 *   2. "Neuer Versand" composer — segmented channel toggle + template chips
 *      + primary action. Always pinned on top.
 *   3. "Verlauf" — vertical timeline of historic operations (newest first).
 *      Each entry collapses to a single line by default; the most recent entry
 *      auto-expands so the user lands on the live state at a glance.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowForward,
  IconBell,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconClock,
  IconDownload,
  IconExternalLink,
  IconLoader2,
  IconMail,
  IconMailOpened,
  IconPaperclip,
  IconPencil,
  IconPlus,
  IconSend,
  IconTrash,
} from "@tabler/icons-react"
import clsx from "clsx"
import { useCallback, useEffect, useMemo, useState } from "react"
import { api } from "../../../../api/client"
import type { ExportOperation, ExportOpState } from "./exportOperationsStore"
import {
  effectiveState,
  isOpenState,
  operationsForRecord,
  useExporterIntegrationStatus,
  useExportOperationsStore,
} from "./exportOperationsStore"
import { useStore } from "../../../../store/useStore"

export type DispatchKind = "download" | "email"

interface VersandCockpitProps {
  apId: string
  apTitle: string
  recordId: string
  recordName: string
  recordTypeLabel: string
  /** Optional list of templates configured for this AP. Phase A: typically one. */
  templates: { id: string; title: string }[]
  onBack: () => void
  onOpenDetail: (recordId: string) => void
  onStartDispatch: (params: {
    kind: DispatchKind
    templateIds: string[]
    followUpOf?: string
  }) => void
  /** Opens the same preview / composer UI in read-only mode, prefilled from the historic op. */
  onOpenCase: (op: ExportOperation) => void
  /** Open the composer to continue editing a draft. */
  onEditDraft?: (op: ExportOperation) => void
  /** Drop the draft from the timeline without sending. */
  onDiscardDraft?: (op: ExportOperation) => void
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleDateString("de-DE")
  } catch {
    return iso
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    const d = new Date(iso)
    return (
      d.toLocaleDateString("de-DE") +
      " · " +
      d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    )
  } catch {
    return iso
  }
}

function formatRelative(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return "—"
  try {
    const ms = now.getTime() - new Date(iso).getTime()
    const min = Math.round(ms / 60_000)
    if (min < 1) return "just now"
    if (min < 60) return `${min} min. ago`
    const hr = Math.round(min / 60)
    if (hr < 24) return `${hr} h ago`
    const d = Math.round(hr / 24)
    if (d < 7) return `${d} day${d === 1 ? "" : "s"} ago`
    if (d < 30) return `${Math.round(d / 7)} wk ago`
    return formatDate(iso)
  } catch {
    return iso
  }
}

function daysUntil(
  iso: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - now.getTime()
  return Math.round(ms / (1000 * 60 * 60 * 24))
}

export function StateIcon({
  state,
  size = 13,
}: {
  state: ExportOpState
  size?: number
}) {
  if (state === "answered_ok")
    return <IconCheck size={size} className="text-green-400 shrink-0" />
  if (state === "answered_followup")
    return (
      <IconAlertTriangle size={size} className="text-orange-400 shrink-0" />
    )
  if (state === "answered_received")
    return <IconMailOpened size={size} className="text-blue-400 shrink-0" />
  if (state === "waiting")
    return <IconClock size={size} className="shrink-0 text-yellow1" />
  if (state === "due")
    return <IconAlertCircle size={size} className="text-red-400 shrink-0" />
  if (state === "draft")
    return <IconPencil size={size} className="text-purple-300 shrink-0" />
  return <IconDownload size={size} className="shrink-0 text-grey5" />
}

export const STATE_DISPLAY: Record<ExportOpState, { cls: string }> = {
  draft: { cls: "text-purple-300" },
  answered_ok: { cls: "text-green-400" },
  answered_followup: { cls: "text-orange-400" },
  answered_received: { cls: "text-blue-400" },
  waiting: { cls: "text-yellow1" },
  due: { cls: "text-red-400" },
  closed: { cls: "text-grey5" },
}

export const STATE_LABEL_KEYS: Record<
  ExportOpState,
  | "QMS/COCKPIT/STATE_DRAFT"
  | "QMS/COCKPIT/STATE_ANSWERED_OK"
  | "QMS/COCKPIT/STATE_ANSWERED_FOLLOWUP"
  | "QMS/COCKPIT/STATE_ANSWERED_RECEIVED"
  | "QMS/COCKPIT/STATE_WAITING"
  | "QMS/COCKPIT/STATE_DUE"
  | "QMS/COCKPIT/STATE_CLOSED"
> = {
  draft: "QMS/COCKPIT/STATE_DRAFT",
  answered_ok: "QMS/COCKPIT/STATE_ANSWERED_OK",
  answered_followup: "QMS/COCKPIT/STATE_ANSWERED_FOLLOWUP",
  answered_received: "QMS/COCKPIT/STATE_ANSWERED_RECEIVED",
  waiting: "QMS/COCKPIT/STATE_WAITING",
  due: "QMS/COCKPIT/STATE_DUE",
  closed: "QMS/COCKPIT/STATE_CLOSED",
}

const STATE_DOT: Record<ExportOpState, { ring: string; bg: string }> = {
  draft: { ring: "ring-purple-400/40", bg: "bg-purple-500/15" },
  answered_ok: { ring: "ring-green-400/30", bg: "bg-green-400/15" },
  answered_followup: { ring: "ring-orange-400/30", bg: "bg-orange-400/15" },
  answered_received: { ring: "ring-blue-400/30", bg: "bg-blue-400/15" },
  waiting: { ring: "ring-yellow1/30", bg: "bg-yellow1/15" },
  due: { ring: "ring-red-400/30", bg: "bg-red-400/15" },
  closed: { ring: "ring-grey5/30", bg: "bg-grey3" },
}

// ── Timeline node model ────────────────────────────────────────────────────────
// Operations are grouped into nodes:
//   • 'pdf'           — single-op terminal node (lightweight)
//   • 'email-thread'  — full thread: initial template + all follow-up emails,
//                       always rendered as ONE node regardless of open/closed.

type TimelineNodeKind = "pdf" | "email-thread" | "draft"

interface TimelineNode {
  /** Stable id (= latest op id) used for expansion tracking. */
  id: string
  kind: TimelineNodeKind
  /** Newest-first; multiple ops only for email-thread. */
  ops: ExportOperation[]
  latestOp: ExportOperation
  state: ExportOpState
  isOpen: boolean
}

function buildTimelineNodes(ops: ExportOperation[]): TimelineNode[] {
  const opsById = new Map(ops.map((op) => [op.id, op]))
  const findRoot = (op: ExportOperation): ExportOperation => {
    let cur = op
    let guard = 0
    while (cur.follow_up_of && opsById.has(cur.follow_up_of) && guard++ < 100) {
      cur = opsById.get(cur.follow_up_of)!
    }
    return cur
  }

  const emailGroups = new Map<string, ExportOperation[]>()
  for (const op of ops) {
    if (op.channel !== "email") continue
    if (op.state === "draft") continue // drafts render as their own node
    const root = findRoot(op)
    const list = emailGroups.get(root.id) ?? []
    list.push(op)
    emailGroups.set(root.id, list)
  }

  const nodes: TimelineNode[] = []

  for (const op of ops) {
    if (op.state === "draft") {
      nodes.push({
        id: op.id,
        kind: "draft",
        ops: [op],
        latestOp: op,
        state: "draft",
        isOpen: true,
      })
      continue
    }
    if (op.channel === "download") {
      nodes.push({
        id: op.id,
        kind: "pdf",
        ops: [op],
        latestOp: op,
        state: effectiveState(op),
        isOpen: false,
      })
    }
  }

  for (const members of emailGroups.values()) {
    members.sort((a, b) => (b.created_at < a.created_at ? -1 : 1)) // newest first
    const latest = members[0]
    const state = effectiveState(latest)
    nodes.push({
      id: latest.id,
      kind: "email-thread",
      ops: members,
      latestOp: latest,
      state,
      isOpen: isOpenState(state),
    })
  }

  nodes.sort((a, b) => (b.latestOp.created_at < a.latestOp.created_at ? -1 : 1))
  return nodes
}

export function VersandCockpit({
  apId,
  recordId,
  recordName,
  recordTypeLabel,
  templates,
  onBack,
  onOpenDetail,
  onStartDispatch,
  onOpenCase,
  onEditDraft,
  onDiscardDraft,
}: VersandCockpitProps) {
  const { translate } = useTranslation()
  const operations = useExportOperationsStore((s) => s.operations)
  const setReply = useExportOperationsStore((s) => s.setReply)
  const remind = useExportOperationsStore((s) => s.remind)
  const simulateReply = useExportOperationsStore((s) => s.simulateReply)
  const forwardToImporter = useExportOperationsStore((s) => s.forwardToImporter)
  const replyDetectionOpId = useExportOperationsStore(
    (s) => s.replyDetectionOpId,
  )
  const integration = useExporterIntegrationStatus()
  const selectRecords = useStore((s) => s.selectRecords)

  const cases = useMemo(
    () => operationsForRecord(operations, apId, recordId),
    [operations, apId, recordId],
  )

  const timelineNodes = useMemo(() => buildTimelineNodes(cases), [cases])
  const latestNodeId = timelineNodes[0]?.id ?? null

  // Expanded set — auto-open the most recent node whenever it changes.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(latestNodeId ? [latestNodeId] : []),
  )
  useEffect(() => {
    if (!latestNodeId) return
    setExpandedIds((prev) =>
      prev.has(latestNodeId) ? prev : new Set([latestNodeId, ...prev]),
    )
  }, [latestNodeId])

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Thread rules: a record can only have one open email thread at a time.
  // While that thread is open, "Neuer Eintrag" is locked. PDF is always allowed
  // — it's a lightweight terminal node that doesn't open a thread.
  const hasOpenEmailThread = useMemo(
    () =>
      cases.some(
        (op) =>
          op.channel === "email" &&
          op.state !== "draft" && // a draft doesn't lock the thread starter
          isOpenState(effectiveState(op)),
      ),
    [cases],
  )

  // Templates are configured once in AP settings — both sequence start and PDF
  // export use the full configured set; no per-send chooser.
  const configuredTemplateIds = useMemo(
    () => templates.map((t) => t.id),
    [templates],
  )
  const [forwardingOpId, setForwardingOpId] = useState<string | null>(null)
  const [forwardResult, setForwardResult] = useState<{
    opId: string
    pendingDocId: string
  } | null>(null)

  const handleSendEmail = useCallback(() => {
    if (configuredTemplateIds.length === 0) return
    onStartDispatch({ kind: "email", templateIds: configuredTemplateIds })
  }, [configuredTemplateIds, onStartDispatch])

  const handleDownloadPdf = useCallback(() => {
    if (configuredTemplateIds.length === 0) return
    onStartDispatch({ kind: "download", templateIds: configuredTemplateIds })
  }, [configuredTemplateIds, onStartDispatch])

  const handleNachfass = useCallback(
    (op: ExportOperation) => {
      onStartDispatch({
        kind: "email",
        templateIds:
          op.template_ids.length > 0 ? op.template_ids : configuredTemplateIds,
        followUpOf: op.id,
      })
    },
    [configuredTemplateIds, onStartDispatch],
  )

  const handleRemind = useCallback(
    (opId: string) => {
      remind(apId, opId).catch((err) => {
        window.alert(
          err instanceof Error ? err.message : translate("QMS/COCKPIT/ERR_REMIND_FAILED"),
        )
      })
    },
    [apId, remind],
  )

  const handleSimulateReply = useCallback(
    (opId: string) => {
      simulateReply(apId, opId, recordName).catch((err) => {
        window.alert(
          err instanceof Error
            ? err.message
            : translate("QMS/COCKPIT/ERR_SIMULATE_FAILED"),
        )
      })
    },
    [apId, recordName, simulateReply],
  )

  const handleMarkFinished = useCallback(
    (opId: string) => {
      setReply(apId, opId, { kind: "ok" }).catch((err) => {
        window.alert(
          err instanceof Error
            ? err.message
            : translate("QMS/COCKPIT/ERR_COMPLETE_FAILED"),
        )
      })
    },
    [apId, setReply],
  )

  const handleForward = useCallback(
    async (op: ExportOperation) => {
      setForwardingOpId(op.id)
      try {
        const out = await forwardToImporter(apId, op.id, "supplier")
        // Ensure a bucket renders for the forwarded docs: the importer AP only
        // shows a bucket per *selected* supplier, so the target supplier must be
        // added to the global selection. Without this, the docs land with a
        // target_record_id (so they don't appear under "Unassigned") but no
        // bucket exists for that supplier, leaving the docs invisible.
        if (out.target_record_id) selectRecords([out.target_record_id])
        setForwardResult({ opId: op.id, pendingDocId: out.pending_document_id })
      } catch (err) {
        window.alert(
          err instanceof Error
            ? err.message
            : translate("QMS/COCKPIT/ERR_FORWARD_FAILED"),
        )
      } finally {
        setForwardingOpId(null)
      }
    },
    [apId, forwardToImporter, selectRecords, translate],
  )

  const handleNachfassFromReceived = useCallback(
    async (op: ExportOperation) => {
      try {
        await setReply(apId, op.id, {
          kind: "followup",
          reason: translate("QMS/COCKPIT/FOLLOWUP_AFTER_REPLY_REASON"),
        })
      } catch (err) {
        window.alert(
          err instanceof Error
            ? err.message
            : translate("QMS/COCKPIT/ERR_UPDATE_FAILED"),
        )
        return
      }
      handleNachfass(op)
    },
    [apId, setReply, handleNachfass, translate],
  )

  // Suppress unused warning — replyDetectionOpId is consumed by sub-components
  void replyDetectionOpId

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-grey4 px-4 py-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-grey5 transition-colors hover:text-white"
        >
          <IconChevronLeft size={14} />
          {translate("QMS/COCKPIT/BACK")}
        </button>
        <span className="mx-1 text-xs text-grey5">|</span>
        <span className="truncate text-sm font-medium text-white">
          {recordName}
        </span>
        <span className="text-xs text-grey5">({recordTypeLabel})</span>
        <div className="flex-1" />
        <button
          onClick={() => onOpenDetail(recordId)}
          className="flex items-center gap-1 rounded border border-grey4 px-2 py-1 text-[11px] text-grey5 transition-colors hover:border-grey5 hover:text-white"
        >
          <IconExternalLink size={11} />
          {translate("QMS/COCKPIT/OPEN_RECORD")}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {integration.mode === "mock" && (
          <div className="border-purple-500/40 bg-purple-500/10 text-purple-200 mb-4 rounded-lg border px-3 py-2 text-[11px]">
            {translate("QMS/COCKPIT/DEMO_MODE_BANNER")}
          </div>
        )}

        <ol className="relative">
          {/* Continuous rail */}
          <div
            className="absolute bottom-3 left-[15px] top-3 w-px bg-grey4/60"
            aria-hidden
          />

          <CreateZone
            hasOpenEmailThread={hasOpenEmailThread}
            templates={templates}
            onSendEmail={handleSendEmail}
            onDownloadPdf={handleDownloadPdf}
          />

          {cases.length === 0 ? (
            <li className="relative pb-2 pl-10 pt-3">
              <p className="text-[13px] italic text-grey5/90">
                {translate("QMS/COCKPIT/NO_HISTORY_YET")}
              </p>
            </li>
          ) : (
            timelineNodes.map((node, idx) => (
              <TimelineNodeView
                key={node.id}
                apId={apId}
                node={node}
                templates={templates}
                expanded={expandedIds.has(node.id)}
                onToggle={() => toggleExpanded(node.id)}
                isFirst={idx === 0}
                onOpenCase={onOpenCase}
                onForwardToImporter={handleForward}
                onNachfassFromReceived={handleNachfassFromReceived}
                onRemind={handleRemind}
                onNachfass={handleNachfass}
                onSimulateReply={handleSimulateReply}
                onMarkFinished={handleMarkFinished}
                onEditDraft={onEditDraft}
                onDiscardDraft={onDiscardDraft}
                isForwarding={forwardingOpId === node.latestOp.id}
                forwardJustDoneFor={
                  forwardResult?.opId === node.latestOp.id
                    ? forwardResult.pendingDocId
                    : null
                }
              />
            ))
          )}
        </ol>
      </div>
    </div>
  )
}

// ── Create zone — inline action buttons next to the + rail marker ─────────────

interface CreateZoneProps {
  hasOpenEmailThread: boolean
  templates: { id: string; title: string }[]
  onSendEmail: () => void
  onDownloadPdf: () => void
}

function CreateZone({
  hasOpenEmailThread,
  templates,
  onSendEmail,
  onDownloadPdf,
}: CreateZoneProps) {
  const { translate } = useTranslation()
  const noTemplates = templates.length === 0
  const emailDisabled = hasOpenEmailThread || noTemplates
  const pdfDisabled = noTemplates

  return (
    <li className="relative pb-5 pl-10">
      <span
        className="bg-grey0 absolute left-0 top-1.5 flex h-[30px] w-[30px] items-center justify-center rounded-full border border-dashed border-yellow1/60 text-yellow1"
        aria-hidden
      >
        <IconPlus size={14} />
      </span>

      <div className="flex flex-wrap items-center gap-2 py-1.5">
        <button
          onClick={onSendEmail}
          disabled={emailDisabled}
          title={
            hasOpenEmailThread
              ? translate("QMS/COCKPIT/TOOLTIP_SEQ_OPEN")
              : noTemplates
                ? translate("QMS/COCKPIT/TOOLTIP_NO_TEMPLATE")
                : undefined
          }
          className={clsx(
            "flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[12px] font-semibold transition-all",
            emailDisabled
              ? "cursor-not-allowed bg-grey3 text-grey5"
              : "hover:bg-yellow-300 bg-yellow1 text-grey1 shadow-xs shadow-yellow1/30",
          )}
        >
          <IconSend size={13} />
          {translate("QMS/COCKPIT/BTN_START_EMAIL_SEQ")}
          {hasOpenEmailThread && (
            <span className="ml-1 rounded bg-grey4/60 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-grey5">
              {translate("QMS/COCKPIT/BTN_EMAIL_SEQ_LOCKED")}
            </span>
          )}
        </button>

        <button
          onClick={onDownloadPdf}
          disabled={pdfDisabled}
          title={
            noTemplates
              ? translate("QMS/COCKPIT/TOOLTIP_NO_TEMPLATE")
              : undefined
          }
          className="flex items-center gap-1.5 rounded-md border border-grey4 bg-grey2 px-3.5 py-1.5 text-[12px] text-white transition-colors hover:border-grey5 hover:bg-grey3 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <IconDownload size={13} />
          {translate("QMS/COCKPIT/BTN_CREATE_PDF")}
        </button>
      </div>
    </li>
  )
}

// ── Node dispatcher ────────────────────────────────────────────────────────────

interface TimelineNodeViewProps {
  apId: string
  node: TimelineNode
  templates: { id: string; title: string }[]
  expanded: boolean
  onToggle: () => void
  isFirst: boolean
  onOpenCase: (op: ExportOperation) => void
  onForwardToImporter: (op: ExportOperation) => void
  onNachfassFromReceived: (op: ExportOperation) => void
  onRemind: (opId: string) => void
  onNachfass: (op: ExportOperation) => void
  onSimulateReply: (opId: string) => void
  onMarkFinished: (opId: string) => void
  onEditDraft?: (op: ExportOperation) => void
  onDiscardDraft?: (op: ExportOperation) => void
  isForwarding: boolean
  forwardJustDoneFor: string | null
}

function TimelineNodeView(props: TimelineNodeViewProps) {
  const { node } = props
  if (node.kind === "draft") {
    return (
      <DraftView
        node={node}
        templates={props.templates}
        isFirst={props.isFirst}
        onEdit={() => props.onEditDraft?.(node.latestOp)}
        onDiscard={() => props.onDiscardDraft?.(node.latestOp)}
      />
    )
  }
  if (node.kind === "email-thread") {
    return <ThreadView {...props} />
  }
  // pdf — single op, lightweight
  const op = node.latestOp
  return (
    <TimelineEntry
      apId={props.apId}
      op={op}
      templates={props.templates}
      expanded={props.expanded}
      onToggle={props.onToggle}
      isFirst={props.isFirst}
      onOpenCase={() => props.onOpenCase(op)}
      onForwardToImporter={() => props.onForwardToImporter(op)}
      onNachfassFromReceived={() => props.onNachfassFromReceived(op)}
      onRemind={() => props.onRemind(op.id)}
      onNachfass={() => props.onNachfass(op)}
      onSimulateReply={() => props.onSimulateReply(op.id)}
      onMarkFinished={() => props.onMarkFinished(op.id)}
      isForwarding={props.isForwarding}
      forwardJustDoneFor={props.forwardJustDoneFor}
    />
  )
}

// ── Draft view — uneditable summary + Bearbeiten / Verwerfen buttons ─────────

function DraftView({
  node,
  templates,
  isFirst,
  onEdit,
  onDiscard,
}: {
  node: TimelineNode
  templates: { id: string; title: string }[]
  isFirst: boolean
  onEdit: () => void
  onDiscard: () => void
}) {
  const { translate } = useTranslation()
  const op = node.latestOp
  const meta = STATE_DISPLAY.draft
  const dot = STATE_DOT.draft
  const templateLabel =
    op.template_ids
      .map((tid) => templates.find((t) => t.id === tid)?.title ?? tid)
      .join(", ") || translate("QMS/COCKPIT/TEMPLATE_FALLBACK")

  return (
    <li className="relative pb-3 pl-10">
      <span
        className={clsx(
          "absolute left-0 top-1.5 flex h-[30px] w-[30px] items-center justify-center rounded-full ring-2",
          dot.bg,
          dot.ring,
          isFirst ? "ring-offset-grey0 ring-offset-1" : "",
        )}
        aria-hidden
      >
        <StateIcon state="draft" size={14} />
      </span>

      <div className="border-purple-500/40 bg-purple-500/[0.04] rounded-lg border">
        <div className="flex items-center gap-2 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-xs text-white">
              <span className="font-medium">
                {translate("QMS/COCKPIT/DRAFT_LABEL")}
              </span>
              <span className="text-grey5">·</span>
              <span className="truncate text-grey6">{templateLabel}</span>
            </p>
            <p className="mt-0.5 text-[10.5px] text-grey5">
              {op.email_to ? (
                <>
                  {translate("QMS/COCKPIT/META_TO")}{" "}
                  <span className="text-grey6">{op.email_to}</span>
                </>
              ) : (
                <>{translate("QMS/COCKPIT/DRAFT_NO_RECIPIENT")}</>
              )}
            </p>
          </div>
          <span
            className={clsx(
              "rounded-full bg-grey2 px-2 py-0.5 text-[10px] font-medium",
              meta.cls,
            )}
          >
            {translate(STATE_LABEL_KEYS["draft"])}
          </span>
        </div>

        <div className="border-purple-500/20 flex items-center gap-2 border-t px-3 py-2">
          <button
            onClick={onEdit}
            className="hover:bg-yellow-300 flex items-center gap-1.5 rounded-md bg-yellow1 px-3 py-1.5 text-[12px] font-semibold text-grey1 transition-colors"
          >
            <IconPencil size={13} />
            {translate("QMS/COCKPIT/BTN_OPEN_DRAFT")}
          </button>
          <button
            onClick={onDiscard}
            className="flex items-center gap-1 rounded-md border border-grey4 px-2.5 py-1 text-[11px] text-grey5 transition-colors hover:border-grey5 hover:text-white"
          >
            <IconTrash size={11} />
            {translate("QMS/COCKPIT/BTN_DISCARD")}
          </button>
        </div>
      </div>
    </li>
  )
}

// ── Timeline entry (single op) ─────────────────────────────────────────────────

interface TimelineEntryProps {
  apId: string
  op: ExportOperation
  templates: { id: string; title: string }[]
  expanded: boolean
  onToggle: () => void
  isFirst: boolean
  onOpenCase: () => void
  onForwardToImporter: () => void
  onNachfassFromReceived: () => void
  onRemind: () => void
  onNachfass: () => void
  onSimulateReply: () => void
  onMarkFinished: () => void
  isForwarding: boolean
  forwardJustDoneFor: string | null
}

function TimelineEntry({
  apId,
  op,
  templates,
  expanded,
  onToggle,
  isFirst,
  onOpenCase,
  onForwardToImporter,
  onNachfassFromReceived,
  onRemind,
  onNachfass,
  onSimulateReply,
  onMarkFinished,
  isForwarding,
  forwardJustDoneFor,
}: TimelineEntryProps) {
  const { translate } = useTranslation()
  const state = effectiveState(op)
  const meta = STATE_DISPLAY[state]
  const dot = STATE_DOT[state]
  const isReplyDetecting = useExportOperationsStore(
    (s) => s.replyDetectionOpId === op.id,
  )

  const templateLabel =
    op.template_ids
      .map((tid) => templates.find((t) => t.id === tid)?.title ?? tid)
      .join(", ") || translate("QMS/COCKPIT/TEMPLATE_FALLBACK")
  const channelLabel =
    op.channel === "email"
      ? translate("QMS/COCKPIT/CHANNEL_EMAIL")
      : translate("QMS/COCKPIT/CHANNEL_PDF")
  const dueDays =
    state === "waiting" || state === "due" ? daysUntil(op.due_at ?? null) : null

  // One-line summary for the collapsed row.
  let summary: React.ReactNode = null
  if (state === "waiting") {
    summary = op.due_at ? (
      <>
        {translate("QMS/COCKPIT/SUMMARY_FOLLOWUP_IN", {
          date: formatDate(op.due_at),
          n: dueDays !== null && dueDays > 0 ? dueDays : 0,
          unit:
            dueDays === 1
              ? translate("QMS/COCKPIT/THREAD_STEP_SINGULAR")
              : translate("QMS/COCKPIT/THREAD_STEP_PLURAL"),
        })}
      </>
    ) : (
      <>{translate("QMS/COCKPIT/STATE_WAITING")}</>
    )
  } else if (state === "due") {
    summary =
      op.due_at && dueDays !== null && dueDays < 0 ? (
        <>
          {translate("QMS/COCKPIT/SUMMARY_DUE_SINCE", {
            n: Math.abs(dueDays),
            unit:
              Math.abs(dueDays) === 1
                ? translate("QMS/COCKPIT/THREAD_STEP_SINGULAR")
                : translate("QMS/COCKPIT/THREAD_STEP_PLURAL"),
          })}
        </>
      ) : (
        <>{translate("QMS/COCKPIT/SUMMARY_DUE_TODAY")}</>
      )
  } else if (state === "answered_received") {
    summary = op.response_from ? (
      <>
        {translate("QMS/COCKPIT/SUMMARY_REPLY_FROM", {
          from: op.response_from,
        })}
      </>
    ) : (
      <>{translate("QMS/COCKPIT/STATE_ANSWERED_RECEIVED")}</>
    )
  } else if (state === "answered_ok") {
    summary = <>{translate("QMS/COCKPIT/SUMMARY_ANSWERED_OK")}</>
  } else if (state === "answered_followup") {
    summary = <>{translate("QMS/COCKPIT/SUMMARY_FOLLOWUP_REQ")}</>
  } else if (state === "closed") {
    summary = (
      <>
        {translate("QMS/COCKPIT/SUMMARY_PDF_DOWNLOADED")}
        {op.created_by ? ` (${op.created_by})` : ""}
      </>
    )
  }

  return (
    <li className="relative pb-3 pl-10 last:pb-0">
      {/* Dot marker over the rail */}
      <button
        type="button"
        onClick={onToggle}
        title={
          expanded
            ? translate("QMS/COCKPIT/TOGGLE_COLLAPSE")
            : translate("QMS/COCKPIT/TOGGLE_EXPAND")
        }
        className={clsx(
          "absolute left-0 top-1.5 flex h-[30px] w-[30px] items-center justify-center rounded-full ring-2 transition-all",
          dot.bg,
          dot.ring,
          isFirst ? "ring-offset-grey0 ring-offset-1" : "",
        )}
      >
        <StateIcon state={state} size={14} />
      </button>

      {/* Row content */}
      <div
        className={clsx(
          "rounded-lg border transition-colors",
          expanded
            ? "border-grey4 bg-grey1"
            : "border-grey4/60 bg-grey1/50 hover:border-grey4 hover:bg-grey1",
        )}
      >
        {/* Header (always visible — click toggles) */}
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
        >
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-xs text-white">
              <span className="font-medium">{channelLabel}</span>
              <span className="text-grey5">·</span>
              <span className="truncate text-grey6">{templateLabel}</span>
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-grey5">
              <span>{formatRelative(op.created_at)}</span>
              {summary && (
                <>
                  <span className="text-grey4">·</span>
                  <span className={clsx("truncate", meta.cls)}>{summary}</span>
                </>
              )}
            </p>
          </div>
          <span
            className={clsx(
              "rounded-full bg-grey2 px-2 py-0.5 text-[10px] font-medium",
              meta.cls,
            )}
          >
            {translate(STATE_LABEL_KEYS[state])}
          </span>
          <IconChevronDown
            size={14}
            className={clsx(
              "shrink-0 text-grey5 transition-transform",
              expanded ? "rotate-180" : "",
            )}
          />
        </button>

        {/* Expanded body */}
        {expanded && (
          <div className="space-y-2 border-t border-grey4/50 px-3 py-3">
            {/* Meta strip */}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10.5px] text-grey5">
              <span>{formatDateTime(op.created_at)}</span>
              {op.email_to && (
                <span>
                  {translate("QMS/COCKPIT/META_TO")}{" "}
                  <span className="text-grey6">{op.email_to}</span>
                </span>
              )}
              {op.created_by && (
                <span>
                  {translate("QMS/COCKPIT/META_FROM")}{" "}
                  <span className="text-grey6">{op.created_by}</span>
                </span>
              )}
              {op.follow_up_of && (
                <span className="text-orange-300/80">
                  {translate("QMS/COCKPIT/META_FOLLOWUP_TAG")}
                </span>
              )}
            </div>

            {/* State-specific detail */}
            {(state === "waiting" || state === "due") && op.due_at && (
              <p
                className={clsx(
                  "text-[11px]",
                  state === "due" ? "text-red-400" : "text-yellow1/90",
                )}
              >
                {state === "due"
                  ? translate("QMS/COCKPIT/STATE_DUE_DETAIL")
                  : translate("QMS/COCKPIT/STATE_WAITING")}{" "}
                — {translate("QMS/COMPOSER/SECTION_FOLLOWUP")}{" "}
                {formatDate(op.due_at)}
                {dueDays !== null &&
                  (dueDays > 0 ? (
                    <span className="text-grey5">
                      {" "}
                      · {translate("QMS/COCKPIT/DUE_IN_N_DAYS", { n: dueDays, unit: dueDays === 1 ? translate("QMS/COCKPIT/THREAD_STEP_SINGULAR") : translate("QMS/COCKPIT/THREAD_STEP_PLURAL") })}
                    </span>
                  ) : dueDays < 0 ? (
                    <span>
                      {" "}
                      · {translate("QMS/COCKPIT/DUE_SINCE_N_DAYS", { n: Math.abs(dueDays), unit: Math.abs(dueDays) === 1 ? translate("QMS/COCKPIT/THREAD_STEP_SINGULAR") : translate("QMS/COCKPIT/THREAD_STEP_PLURAL") })}
                    </span>
                  ) : (
                    <span> · {translate("QMS/COCKPIT/DUE_TODAY")}</span>
                  ))}
              </p>
            )}

            {state === "answered_received" &&
              (op.response_subject ||
                op.response_body_text ||
                op.has_response_attachment) && (
                <div className="border-blue-500/30 bg-blue-500/5 space-y-1.5 rounded-md border p-2.5 text-[11px]">
                  <p className="text-blue-300/80 text-[10px] font-medium uppercase tracking-wider">
                    {translate("QMS/COCKPIT/REPLY_SECTION_HEADER", {
                      date: formatDate(op.response_received_at),
                    })}
                  </p>
                  {op.response_subject && (
                    <p className="text-grey6">
                      <span className="text-grey5">
                        {translate("QMS/COCKPIT/REPLY_SUBJECT")}
                      </span>{" "}
                      <span className="text-white">{op.response_subject}</span>
                    </p>
                  )}
                  {op.response_body_text && (
                    <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap font-sans text-grey6">
                      {op.response_body_text}
                    </pre>
                  )}
                  {(() => {
                    const attachments = op.response_attachments ?? []
                    if (attachments.length > 0) {
                      return (
                        <div className="flex flex-wrap gap-1">
                          {attachments.map((att) => (
                            <a
                              key={att.id}
                              href={api.exporterOperationsResponseAttachmentUrl(
                                apId,
                                op.id,
                                att.id,
                              )}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors"
                            >
                              <IconPaperclip size={10} />
                              {att.filename}
                            </a>
                          ))}
                        </div>
                      )
                    }
                    if (op.has_response_attachment && op.response_file_name) {
                      return (
                        <a
                          href={api.exporterOperationsAttachmentUrl(
                            apId,
                            op.id,
                          )}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors"
                        >
                          <IconPaperclip size={10} />
                          {op.response_file_name}
                        </a>
                      )
                    }
                    return null
                  })()}
                </div>
              )}

            {state === "answered_followup" && op.response_summary && (
              <p className="text-[11px] italic text-grey6">
                „{op.response_summary}"
              </p>
            )}

            {state === "answered_ok" && op.response_received_at && (
              <p className="text-green-400/90 text-[11px]">
                {translate("QMS/COCKPIT/ANSWERED_OK_DETAIL", {
                  date: formatDate(op.response_received_at),
                })}
              </p>
            )}

            {/* Actions — hierarchy: primary (filled) > secondary (outlined) > tertiary (ghost) */}
            <div className="flex flex-wrap items-center gap-2 pt-2">
              {/* Primary action — most prominent, action the user is most likely to take next */}
              {state === "answered_received" && (
                <>
                  <ActionButton
                    variant="primary"
                    tone="green"
                    onClick={onMarkFinished}
                    icon={<IconCheck size={14} />}
                    label={translate("QMS/COCKPIT/BTN_COMPLETE_OP")}
                    title={translate("QMS/COCKPIT/BTN_COMPLETE_OP_TITLE")}
                  />
                  <ActionButton
                    variant="secondary"
                    tone="yellow"
                    onClick={onForwardToImporter}
                    disabled={isForwarding || !op.has_response_attachment}
                    title={
                      op.has_response_attachment
                        ? translate("QMS/COCKPIT/TOOLTIP_FORWARD")
                        : translate("QMS/COCKPIT/TOOLTIP_NO_DOC_FORWARD")
                    }
                    icon={
                      isForwarding ? (
                        <IconLoader2 size={12} className="animate-spin" />
                      ) : (
                        <IconArrowForward size={12} />
                      )
                    }
                    label={translate("QMS/COCKPIT/BTN_FORWARD_TO_SUPPLIER")}
                  />
                </>
              )}
              {state === "answered_followup" && (
                <ActionButton
                  variant="primary"
                  tone="orange"
                  onClick={onNachfass}
                  icon={<IconMail size={14} />}
                  label={translate("QMS/COCKPIT/BTN_FOLLOWUP_EMAIL")}
                />
              )}
              {state === "due" && (
                <ActionButton
                  variant="primary"
                  tone="red"
                  onClick={onRemind}
                  icon={<IconBell size={14} />}
                  label={translate("QMS/COCKPIT/BTN_SEND_REMINDER")}
                />
              )}

              {/* Secondary actions */}
              {state === "answered_received" && (
                <ActionButton
                  variant="secondary"
                  tone="orange"
                  onClick={onNachfassFromReceived}
                  icon={<IconMail size={12} />}
                  label={translate("QMS/COCKPIT/BTN_FOLLOWUP_SEND")}
                  title={translate("QMS/COCKPIT/TOOLTIP_FOLLOWUP_DETAILS")}
                />
              )}
              {/* Demo lead-in: while the mocked inbound is being "evaluated"
                  we show a prominent in-progress card so the upcoming state
                  flip (waiting → answered_received) doesn't appear out of
                  nowhere. */}
              {(state === "waiting" || state === "due") &&
                op.is_mock &&
                isReplyDetecting && <ReplyDetectionPill />}

              <div className="flex-1" />

              {/* Demo-only simulate reply for mock ops */}
              {(state === "waiting" || state === "due") &&
                op.is_mock &&
                !isReplyDetecting && (
                  <ActionButton
                    variant="tertiary"
                    onClick={onSimulateReply}
                    icon={<IconMailOpened size={11} />}
                    label={translate("QMS/COCKPIT/BTN_SIMULATE_REPLY")}
                    title={translate("QMS/COCKPIT/TOOLTIP_SIMULATE")}
                  />
                )}

              {/* Tertiary — view source artefact (always available) */}
              <ActionButton
                variant="tertiary"
                onClick={onOpenCase}
                icon={<IconExternalLink size={11} />}
                label={
                  op.channel === "email"
                    ? translate("QMS/COCKPIT/BTN_VIEW_EMAIL")
                    : translate("QMS/COCKPIT/BTN_VIEW_PDF")
                }
                title={
                  op.channel === "email"
                    ? translate("QMS/COCKPIT/TOOLTIP_VIEW_SENT_EMAIL")
                    : translate("QMS/COCKPIT/TOOLTIP_VIEW_PDF")
                }
              />
            </div>

            {forwardJustDoneFor && (
              <p className="text-[11px] italic text-yellow1/80">
                {translate("QMS/COCKPIT/FORWARD_SUCCESS")}
              </p>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

// ── ReplyDetectionPill — large in-progress card shown while the mocked
//    inbound is being "evaluated", so the upcoming state flip has visible
//    lead-in and doesn't feel teleported.
function ReplyDetectionPill() {
  const { translate } = useTranslation()
  return (
    <div
      className="border-blue-500/50 bg-blue-500/10 text-blue-200 shadow-blue-500/10 flex w-full items-center gap-3 rounded-lg border px-4 py-3 shadow-xs"
      title={translate("QMS/COCKPIT/TOOLTIP_REPLY_DETECTING")}
    >
      <IconLoader2 size={20} className="text-blue-300 shrink-0 animate-spin" />
      <div className="flex flex-col">
        <span className="text-blue-100 text-[13px] font-semibold">
          {translate("QMS/COCKPIT/REPLY_DETECTING_TITLE")}
        </span>
        <span className="text-blue-200/90 text-[12px]">
          {translate("QMS/COCKPIT/REPLY_DETECTING_BODY")}
        </span>
      </div>
    </div>
  )
}

// ── ActionButton — three variants × four tones ─────────────────────────────────

type ActionVariant = "primary" | "secondary" | "tertiary"
type ActionTone =
  | "yellow"
  | "orange"
  | "red"
  | "purple"
  | "green"
  | "neutral"

interface ActionButtonProps {
  variant: ActionVariant
  tone?: ActionTone
  onClick: () => void
  icon: React.ReactNode
  label: string
  disabled?: boolean
  title?: string
}

const TONE_PRIMARY: Record<ActionTone, string> = {
  yellow:
    "bg-yellow1 text-grey1 hover:bg-yellow-300 shadow-xs shadow-yellow1/30",
  orange:
    "bg-orange-500 text-white hover:bg-orange-400 shadow-xs shadow-orange-500/30",
  red: "bg-red-500 text-white hover:bg-red-400 shadow-xs shadow-red-500/30",
  purple:
    "bg-purple-500 text-white hover:bg-purple-400 shadow-xs shadow-purple-500/30",
  green:
    "bg-green-500 text-white hover:bg-green-400 shadow-xs shadow-green-500/30",
  neutral: "bg-grey4 text-white hover:bg-grey5",
}

const TONE_SECONDARY: Record<ActionTone, string> = {
  yellow: "border-yellow1/50 bg-yellow1/10 text-yellow1 hover:bg-yellow1/20",
  orange:
    "border-orange-500/50 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20",
  red: "border-red-500/50 bg-red-500/10 text-red-300 hover:bg-red-500/20",
  purple:
    "border-purple-500/50 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20",
  green:
    "border-green-500/50 bg-green-500/10 text-green-300 hover:bg-green-500/20",
  neutral:
    "border-grey4 bg-grey2 text-grey6 hover:text-white hover:border-grey5",
}

function ActionButton({
  variant,
  tone = "neutral",
  onClick,
  icon,
  label,
  disabled,
  title,
}: ActionButtonProps) {
  if (variant === "primary") {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={clsx(
          "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none",
          TONE_PRIMARY[tone],
        )}
      >
        {icon}
        {label}
      </button>
    )
  }
  if (variant === "secondary") {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={clsx(
          "flex items-center gap-1 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
          TONE_SECONDARY[tone],
        )}
      >
        {icon}
        {label}
      </button>
    )
  }
  // tertiary — ghost text button
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-grey5 transition-colors hover:text-white disabled:opacity-40"
    >
      {icon}
      {label}
    </button>
  )
}

// ── Thread view (open + closed) ────────────────────────────────────────────────
// Aggregates a chain of email ops (initial + follow-ups) into one node. Sub-
// emails are listed inside; when the thread is open, an action area + inline
// follow-up trigger is shown at the bottom.

function ThreadView({
  apId,
  node,
  templates,
  expanded,
  onToggle,
  isFirst,
  onOpenCase,
  onForwardToImporter,
  onNachfassFromReceived,
  onRemind,
  onNachfass,
  onSimulateReply,
  onMarkFinished,
  isForwarding,
  forwardJustDoneFor,
}: TimelineNodeViewProps) {
  const { translate } = useTranslation()
  const ops = node.ops // newest-first
  const latest = node.latestOp
  const root = ops[ops.length - 1]
  const isReplyDetecting = useExportOperationsStore(
    (s) => s.replyDetectionOpId === latest.id,
  )
  const state = node.state
  const isOpen = node.isOpen
  const meta = STATE_DISPLAY[state]
  const dot = STATE_DOT[state]
  const stepsLabel =
    ops.length > 1
      ? translate("QMS/COCKPIT/THREAD_STEPS", { n: ops.length })
      : null

  const rootTemplateLabel =
    root.template_ids
      .map((tid) => templates.find((t) => t.id === tid)?.title ?? tid)
      .join(", ") || translate("QMS/COCKPIT/TEMPLATE_FALLBACK")

  const dueDays =
    state === "waiting" || state === "due"
      ? daysUntil(latest.due_at ?? null)
      : null

  // Subtitle line summarising the latest activity / state
  let subtitle: React.ReactNode = null
  if (state === "waiting" && latest.due_at) {
    subtitle = (
      <>
        {translate("QMS/COCKPIT/SUMMARY_FOLLOWUP_IN", {
          date: formatDate(latest.due_at),
          n: dueDays !== null && dueDays > 0 ? dueDays : 0,
          unit:
            dueDays === 1
              ? translate("QMS/COCKPIT/THREAD_STEP_SINGULAR")
              : translate("QMS/COCKPIT/THREAD_STEP_PLURAL"),
        })}
      </>
    )
  } else if (state === "due") {
    subtitle =
      dueDays !== null && dueDays < 0 ? (
        <>
          {translate("QMS/COCKPIT/SUMMARY_DUE_SINCE", {
            n: Math.abs(dueDays),
            unit:
              Math.abs(dueDays) === 1
                ? translate("QMS/COCKPIT/THREAD_STEP_SINGULAR")
                : translate("QMS/COCKPIT/THREAD_STEP_PLURAL"),
          })}
        </>
      ) : (
        <>{translate("QMS/COCKPIT/SUMMARY_DUE_TODAY")}</>
      )
  } else if (state === "answered_received") {
    subtitle = latest.response_from ? (
      <>
        {translate("QMS/COCKPIT/SUMMARY_REPLY_FROM", {
          from: latest.response_from,
        })}
      </>
    ) : (
      <>{translate("QMS/COCKPIT/SUB_REPLY_RECEIVED")}</>
    )
  } else if (state === "answered_followup") {
    subtitle = <>{translate("QMS/COCKPIT/STATE_ANSWERED_FOLLOWUP")}</>
  } else if (state === "answered_ok") {
    subtitle = (
      <>
        {translate("QMS/COCKPIT/THREAD_CLOSED_SUBTITLE", {
          date: formatRelative(latest.created_at),
        })}
      </>
    )
  }

  // Whether to show inline "compose follow-up" CTA — outside answered_received
  // (which has its own "Nachfass senden" path) and only when thread is open.
  const showFollowUpCta =
    isOpen &&
    (state === "waiting" || state === "due" || state === "answered_followup")

  return (
    <li className="relative pb-3 pl-10 last:pb-0">
      <button
        type="button"
        onClick={onToggle}
        title={
          expanded
            ? translate("QMS/COCKPIT/TOGGLE_THREAD_COLLAPSE")
            : translate("QMS/COCKPIT/TOGGLE_THREAD_EXPAND")
        }
        className={clsx(
          "absolute left-0 top-1.5 flex h-[30px] w-[30px] items-center justify-center rounded-full ring-2 transition-all",
          dot.bg,
          dot.ring,
          isFirst ? "ring-offset-grey0 ring-offset-1" : "",
        )}
      >
        <StateIcon state={state} size={14} />
      </button>

      <div
        className={clsx(
          "rounded-lg border transition-colors",
          expanded
            ? "border-grey4 bg-grey1"
            : "border-grey4/60 bg-grey1/50 hover:border-grey4 hover:bg-grey1",
        )}
      >
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
        >
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-xs text-white">
              <span className="font-medium">
                {translate("QMS/COCKPIT/THREAD_LABEL")}
              </span>
              <span className="text-grey5">·</span>
              <span className="truncate text-grey6">{rootTemplateLabel}</span>
              {stepsLabel && (
                <>
                  <span className="text-grey5">·</span>
                  <span className="text-grey5">{stepsLabel}</span>
                </>
              )}
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-grey5">
              <span>
                {translate("QMS/COCKPIT/THREAD_STARTED", {
                  date: formatDate(root.created_at),
                })}
              </span>
              {subtitle && (
                <>
                  <span className="text-grey4">·</span>
                  <span className={clsx("truncate", meta.cls)}>{subtitle}</span>
                </>
              )}
            </p>
          </div>
          <span
            className={clsx(
              "rounded-full bg-grey2 px-2 py-0.5 text-[10px] font-medium",
              meta.cls,
            )}
          >
            {translate(STATE_LABEL_KEYS[state])}
          </span>
          <IconChevronDown
            size={14}
            className={clsx(
              "shrink-0 text-grey5 transition-transform",
              expanded ? "rotate-180" : "",
            )}
          />
        </button>

        {expanded && (
          <div className="space-y-3 border-t border-grey4/50 px-3 py-3">
            {/* Sub-timeline: oldest → newest, mirrors how a chat reads */}
            <ol className="relative space-y-2">
              <div
                className="absolute bottom-2 left-[7px] top-2 w-px bg-grey4/50"
                aria-hidden
              />
              {[...ops].reverse().map((subOp) => {
                const subState = effectiveState(subOp)
                const subDot = STATE_DOT[subState]
                const isInitial = !subOp.follow_up_of
                const subTemplateLabel = subOp.template_ids
                  .map(
                    (tid) => templates.find((t) => t.id === tid)?.title ?? tid,
                  )
                  .join(", ")
                return (
                  <li key={subOp.id} className="relative pl-6">
                    <span
                      className={clsx(
                        "absolute left-0 top-0.5 flex h-[15px] w-[15px] items-center justify-center rounded-full ring-1",
                        subDot.bg,
                        subDot.ring,
                      )}
                    >
                      <StateIcon state={subState} size={9} />
                    </span>
                    <div className="flex items-baseline gap-1.5">
                      <p className="text-[11px] text-white">
                        {isInitial
                          ? translate("QMS/COCKPIT/SUB_INITIAL")
                          : translate("QMS/COCKPIT/SUB_FOLLOWUP")}
                        {isInitial && subTemplateLabel && (
                          <>
                            <span className="mx-1 text-grey5">·</span>
                            <span className="text-grey6">
                              {subTemplateLabel}
                            </span>
                          </>
                        )}
                        {!isInitial && (
                          <>
                            <span className="mx-1 text-grey5">·</span>
                            <span className="italic text-grey5">
                              {translate("QMS/COCKPIT/SUB_FREE_TEXT")}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                    <p className="text-[10px] text-grey5">
                      {formatDateTime(subOp.created_at)}
                      {subOp.email_to && (
                        <>
                          {" "}
                          · {translate("QMS/COCKPIT/META_TO")}{" "}
                          <span className="text-grey6">{subOp.email_to}</span>
                        </>
                      )}
                    </p>
                  </li>
                )
              })}

              {/* Latest response (only on the most recent op) */}
              {latest.response_received_at &&
                (state === "answered_received" ||
                  state === "answered_ok" ||
                  state === "answered_followup") && (
                  <li className="relative pl-6">
                    <span
                      className={clsx(
                        "absolute left-0 top-0.5 flex h-[15px] w-[15px] items-center justify-center rounded-full ring-1",
                        "bg-blue-400/15 ring-blue-400/30",
                      )}
                    >
                      <IconMailOpened size={9} className="text-blue-400" />
                    </span>
                    <p className="text-[11px] text-white">
                      {translate("QMS/COCKPIT/SUB_REPLY_RECEIVED")}
                      {latest.response_from && (
                        <>
                          <span className="mx-1 text-grey5">·</span>
                          <span className="text-grey6">
                            {latest.response_from}
                          </span>
                        </>
                      )}
                    </p>
                    <p className="text-[10px] text-grey5">
                      {formatDateTime(latest.response_received_at)}
                    </p>
                    {(latest.response_subject ||
                      latest.response_body_text ||
                      latest.has_response_attachment) && (
                      <div className="border-blue-500/30 bg-blue-500/5 mt-1.5 space-y-1 rounded border p-2 text-[11px]">
                        {latest.response_subject && (
                          <p className="text-grey6">
                            <span className="text-grey5">
                              {translate("QMS/COCKPIT/REPLY_SUBJECT")}
                            </span>{" "}
                            <span className="text-white">
                              {latest.response_subject}
                            </span>
                          </p>
                        )}
                        {latest.response_body_text && (
                          <pre className="max-h-24 overflow-y-auto whitespace-pre-wrap font-sans text-grey6">
                            {latest.response_body_text}
                          </pre>
                        )}
                        {(() => {
                          const attachments = latest.response_attachments ?? []
                          if (attachments.length > 0) {
                            return (
                              <div className="flex flex-wrap gap-1">
                                {attachments.map((att) => (
                                  <a
                                    key={att.id}
                                    href={api.exporterOperationsResponseAttachmentUrl(
                                      apId,
                                      latest.id,
                                      att.id,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors"
                                  >
                                    <IconPaperclip size={10} />
                                    {att.filename}
                                  </a>
                                ))}
                              </div>
                            )
                          }
                          if (
                            latest.has_response_attachment &&
                            latest.response_file_name
                          ) {
                            return (
                              <a
                                href={api.exporterOperationsAttachmentUrl(
                                  apId,
                                  latest.id,
                                )}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors"
                              >
                                <IconPaperclip size={10} />
                                {latest.response_file_name}
                              </a>
                            )
                          }
                          return null
                        })()}
                      </div>
                    )}
                    {state === "answered_followup" &&
                      latest.response_summary && (
                        <p className="text-orange-300/90 mt-1 text-[10.5px] italic">
                          {translate("QMS/COCKPIT/FOLLOWUP_INCOMPLETE_MARKED", {
                            summary: latest.response_summary,
                          })}
                        </p>
                      )}
                  </li>
                )}
            </ol>

            {/* State-aware action area (only when thread is open) */}
            {isOpen && (
              <div className="flex flex-wrap items-center gap-2 border-t border-grey4/30 pt-3">
                {state === "answered_received" && (
                  <>
                    <ActionButton
                      variant="primary"
                      tone="green"
                      onClick={() => onMarkFinished(latest.id)}
                      icon={<IconCheck size={14} />}
                      label={translate("QMS/COCKPIT/BTN_COMPLETE_OP")}
                      title={translate("QMS/COCKPIT/BTN_COMPLETE_OP_TITLE")}
                    />
                    <ActionButton
                      variant="secondary"
                      tone="yellow"
                      onClick={() => onForwardToImporter(latest)}
                      disabled={isForwarding || !latest.has_response_attachment}
                      title={
                        latest.has_response_attachment
                          ? translate("QMS/COCKPIT/TOOLTIP_FORWARD")
                          : translate("QMS/COCKPIT/TOOLTIP_NO_DOC_FORWARD")
                      }
                      icon={
                        isForwarding ? (
                          <IconLoader2 size={12} className="animate-spin" />
                        ) : (
                          <IconArrowForward size={12} />
                        )
                      }
                      label={translate("QMS/COCKPIT/BTN_FORWARD_TO_SUPPLIER")}
                    />
                    <ActionButton
                      variant="secondary"
                      tone="orange"
                      onClick={() => onNachfassFromReceived(latest)}
                      icon={<IconMail size={12} />}
                      label={translate("QMS/COCKPIT/BTN_FOLLOWUP_SEND")}
                      title={translate("QMS/COCKPIT/TOOLTIP_FOLLOWUP_DETAILS")}
                    />
                  </>
                )}

                {state === "due" && (
                  <ActionButton
                    variant="primary"
                    tone="red"
                    onClick={() => onRemind(latest.id)}
                    icon={<IconBell size={14} />}
                    label={translate("QMS/COCKPIT/BTN_SEND_REMINDER")}
                  />
                )}

                {state === "answered_followup" && (
                  <ActionButton
                    variant="primary"
                    tone="orange"
                    onClick={() => onNachfass(latest)}
                    icon={<IconMail size={14} />}
                    label={translate("QMS/COCKPIT/BTN_FOLLOWUP_EMAIL")}
                  />
                )}

                {/* Demo lead-in (see TimelineEntry). */}
                {(state === "waiting" || state === "due") &&
                  latest.is_mock &&
                  isReplyDetecting && <ReplyDetectionPill />}

                {/* Demo simulate reply for mock ops */}
                {(state === "waiting" || state === "due") &&
                  latest.is_mock &&
                  !isReplyDetecting && (
                    <ActionButton
                      variant="tertiary"
                      onClick={() => onSimulateReply(latest.id)}
                      icon={<IconMailOpened size={11} />}
                      label={translate("QMS/COCKPIT/BTN_SIMULATE_REPLY")}
                      title={translate("QMS/COCKPIT/TOOLTIP_SIMULATE")}
                    />
                  )}

                <div className="flex-1" />

                {/* Inline follow-up CTA — write a free-text email within this thread */}
                {showFollowUpCta && state !== "answered_followup" && (
                  <ActionButton
                    variant="secondary"
                    tone="neutral"
                    onClick={() => onNachfass(latest)}
                    icon={<IconSend size={12} />}
                    label={translate("QMS/COCKPIT/BTN_WRITE_FOLLOWUP")}
                    title={translate("QMS/COCKPIT/TOOLTIP_WRITE_FOLLOWUP")}
                  />
                )}

                <ActionButton
                  variant="tertiary"
                  onClick={() => onOpenCase(latest)}
                  icon={<IconExternalLink size={11} />}
                  label={translate("QMS/COCKPIT/BTN_VIEW_LAST_EMAIL")}
                />
              </div>
            )}

            {/* Closed thread — read-only footer */}
            {!isOpen && (
              <div className="flex items-center justify-end border-t border-grey4/30 pt-2">
                <ActionButton
                  variant="tertiary"
                  onClick={() => onOpenCase(latest)}
                  icon={<IconExternalLink size={11} />}
                  label={translate("QMS/COCKPIT/BTN_VIEW_LAST_EMAIL")}
                />
              </div>
            )}

            {forwardJustDoneFor && (
              <p className="text-[11px] italic text-yellow1/80">
                {translate("QMS/COCKPIT/FORWARD_SUCCESS")}
              </p>
            )}
          </div>
        )}
      </div>
    </li>
  )
}
